import { useEffect, useState, type FormEvent } from 'react';
import type { SessionPrincipal } from '@laspalmas-identity/shared';
import type { IdentityTrpcClient } from '../../lib/trpc';
import type { AccountSection } from '../../lib/router';
import { mutationBlockReason } from '../../lib/guards';
import { useI18n } from '../../lib/i18n';
import { useGoogleOAuthAvailable } from '../../lib/google-federation';
import { Icon } from '../../components/icons';
import { Badge, Button, Card, DataTable, Dialog, Field, Input, Notice, PageHeader } from '../../components/ui';
import { RealmFieldInput } from '../../components/RealmFieldInput';

type SelfApplication = { id: string; applicationId?: string; bindingId?: string; name: string; clientId: string; type: string; issuer?: string; launchUrl?: string | null };
type SelfApplicationRow = { application: { id: string; name: string }; binding: { id: string; applicationId: string; clientId: string; type: string; issuer?: string; launchUrl?: string | null } };
type AppGroup = { realm: { id: string; name: string; slug: string }; applications: SelfApplication[] };
type Profile = Awaited<ReturnType<IdentityTrpcClient['self']['profile']['query']>>;
type Realm = Awaited<ReturnType<IdentityTrpcClient['self']['realms']['query']>>[number];
type RealmFieldState = Awaited<ReturnType<IdentityTrpcClient['self']['realmFields']['query']>>;
type RealmField = RealmFieldState['fields'][number];
type AuthMethod = Awaited<ReturnType<IdentityTrpcClient['self']['authMethods']['query']>>[number];
type Session = Awaited<ReturnType<IdentityTrpcClient['self']['sessions']['query']>>[number];
type Consent = { id: string; bindingId?: string; applicationName?: string; clientId?: string; realmName?: string; realmSlug?: string; scopesJson: string; claimsJson: string; grantedAt: string };
type WebIdentity = Awaited<ReturnType<IdentityTrpcClient['self']['webIdentities']['query']>>[number];

export function AccountPage({ section, principal, client }: { section: AccountSection; principal: SessionPrincipal; client: IdentityTrpcClient }) {
  switch (section) {
    case 'apps': return <AppsScreen client={client}/>;
    case 'perfil': return <ProfileScreen client={client}/>;
    case 'metodos': return <MethodsScreen principal={principal} client={client}/>;
    case 'sesions': return <SessionsScreen principal={principal} client={client}/>;
    case 'permisos': return <ConsentsScreen principal={principal} client={client}/>;
    case 'identidades': return <WebIdentitiesScreen principal={principal} client={client}/>;
  }
}

function LoadingCard({ children }: { children: string }) {
  return <Card className="section-card"><p className="loading-inline"><span className="spinner"/> {children}</p></Card>;
}

function EmptyCard({ children }: { children: string }) {
  return <Card className="section-card"><div className="empty-inline"><p>{children}</p></div></Card>;
}

function LoadError({ children }: { children: string }) {
  const { t } = useI18n();
  return <Notice tone="danger" title={t('accountLoadError')}>{children}</Notice>;
}

function AppsScreen({ client }: { client: IdentityTrpcClient }) {
  const { t } = useI18n();
  const [groups, setGroups] = useState<AppGroup[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    client.self.apps.query().then((result) => {
      if (!active) return;
      setGroups(normalizeAppGroups(result as unknown, t('accountRealm')));
      setState('ready');
    }).catch(() => {
      if (active) setState('error');
    });
    return () => { active = false; };
  }, [client, t]);

  return <><PageHeader eyebrow={t('accountMyAccount')} title={t('accountAppsTitle')} description={t('accountAppsDescription')}/>
    {state === 'loading' ? <LoadingCard>{t('accountLoadingApps')}</LoadingCard> : state === 'error' ? <LoadError>{t('accountAppsLoadError')}</LoadError> : groups.length === 0 ? <EmptyCard>{t('accountNoApps')}</EmptyCard> : groups.map((group) => <section className="realm-app-group" key={group.realm.id}><div className="realm-app-heading"><div><p className="eyebrow">{t('accountRealm')}</p><h2>{group.realm.name}</h2></div><Badge tone="blue">{group.realm.slug}</Badge></div><div className="app-grid">{group.applications.map((app) => {
      const typeLabel = app.type === 'public' ? t('accountPublicApplication') : t('accountConfidentialApplication');
      const card = <><span className="app-symbol">{initials(app.name)}</span><span><strong>{app.name}</strong><small>{app.clientId} · {typeLabel}</small></span><Badge>{typeLabel}</Badge></>;
      return app.launchUrl && app.issuer ? <a className="app-card" key={app.bindingId ?? app.id} href={app.launchUrl} data-realm-issuer={app.issuer}>{card}</a> : <div className="app-card" key={app.bindingId ?? app.id}>{card}</div>;
    })}</div></section>)}
  </>;
}

function normalizeAppGroups(value: unknown, realmLabel: string): AppGroup[] {
  if (!Array.isArray(value)) return [];
  if (value.every((item) => item && typeof item === 'object' && ('applications' in item || 'apps' in item))) {
    return value.map((item) => {
      const row = item as { realm?: AppGroup['realm']; realmId?: string; realmName?: string; realmSlug?: string; applications?: Array<SelfApplication | SelfApplicationRow>; apps?: Array<SelfApplication | SelfApplicationRow> };
      const applications = (row.applications ?? row.apps ?? []).map((entry): SelfApplication => 'application' in entry && 'binding' in entry ? { id: entry.application.id, applicationId: entry.application.id, bindingId: entry.binding.id, name: entry.application.name, clientId: entry.binding.clientId, type: entry.binding.type, ...(entry.binding.issuer ? { issuer: entry.binding.issuer } : {}), ...(entry.binding.launchUrl ? { launchUrl: entry.binding.launchUrl } : {}) } : entry);
      return { realm: row.realm ?? { id: row.realmId ?? '', name: row.realmName ?? realmLabel, slug: row.realmSlug ?? '' }, applications };
    }).filter((group) => group.realm.id && group.applications.length);
  }
  const grouped = new Map<string, AppGroup>();
  for (const item of value as Array<SelfApplication & { realmId?: string; realmName?: string; realmSlug?: string }>) {
    if (!item.realmId) continue;
    const group = grouped.get(item.realmId) ?? { realm: { id: item.realmId, name: item.realmName ?? realmLabel, slug: item.realmSlug ?? '' }, applications: [] };
    group.applications.push(item);
    grouped.set(item.realmId, group);
  }
  return [...grouped.values()];
}

function ProfileScreen({ client }: { client: IdentityTrpcClient }) {
  const { t } = useI18n();
  const [profile, setProfile] = useState<Profile>();
  const [displayName, setDisplayName] = useState('');
  const [profileState, setProfileState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [realms, setRealms] = useState<Realm[]>([]);
  const [realmState, setRealmState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let active = true;
    client.self.profile.query().then((result) => {
      if (!active) return;
      setProfile(result);
      setDisplayName(result?.displayName ?? '');
      setProfileState('ready');
    }).catch(() => { if (active) setProfileState('error'); });
    return () => { active = false; };
  }, [client]);

  useEffect(() => {
    let active = true;
    client.self.realms.query().then((rows) => {
      if (!active) return;
      setRealms(rows);
      setRealmState('ready');
    }).catch(() => { if (active) setRealmState('error'); });
    return () => { active = false; };
  }, [client]);

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setSaveMessage('');
    setSaveError('');
    try {
      const updated = await client.self.updateProfile.mutate({ displayName: displayName.trim() || null });
      setProfile(updated);
      setDisplayName(updated?.displayName ?? '');
      setSaveMessage(t('accountProfileSaved'));
    } catch {
      setSaveError(t('accountProfileSaveError'));
    } finally {
      setSaving(false);
    }
  }

  return <><PageHeader eyebrow={t('accountMyAccount')} title={t('accountProfileTitle')} description={t('accountProfileDescription')}/>
    {profileState === 'loading' ? <LoadingCard>{t('accountLoadingProfile')}</LoadingCard> : profileState === 'error' ? <LoadError>{t('accountProfileLoadError')}</LoadError> : !profile ? <EmptyCard>{t('accountNoProfile')}</EmptyCard> : <Card className="section-card"><div className="section-heading"><div><h2>{t('accountGlobalDetails')}</h2><p>{t('accountGlobalDetailsDescription')}</p></div>{profile.emailVerifiedAt ? <Badge tone="success">{t('accountEmailVerified')}</Badge> : <Badge tone="warning">{t('accountEmailUnverified')}</Badge>}</div>
      <form className="form-grid" onSubmit={(event) => void saveProfile(event)}>
        <Field label={t('accountDisplayName')}><Input value={displayName} maxLength={200} disabled={saving} onChange={(event) => setDisplayName(event.target.value)}/></Field>
        <Field label={t('accountEmail')} hint={t('accountEmailHint')}><Input value={profile.email} readOnly/></Field>
        <div className="form-actions full">{saveMessage && <span className="saved-message" role="status"><Icon name="check"/> {saveMessage}</span>}{saveError && <span className="field-error" role="alert">{saveError}</span>}<Button type="submit" disabled={saving}>{saving ? t('accountSaving') : t('accountSaveChanges')}</Button></div>
      </form>
    </Card>}
    {realmState === 'loading' ? <LoadingCard>{t('accountLoadingRealms')}</LoadingCard> : realmState === 'error' ? <LoadError>{t('accountRealmsLoadError')}</LoadError> : realms.length === 0 ? <EmptyCard>{t('accountNoRealms')}</EmptyCard> : realms.map((realm) => <RealmFieldsCard key={realm.id} realm={realm} client={client}/>)}
  </>;
}

function RealmFieldsCard({ realm, client }: { realm: Realm; client: IdentityTrpcClient }) {
  const { t } = useI18n();
  const [fieldState, setFieldState] = useState<RealmFieldState>();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    client.self.realmFields.query({ realmId: realm.id }).then((result) => {
      if (!active) return;
      setFieldState(result);
      setValues(fieldValues(result.fields));
      setState('ready');
    }).catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, [client, realm.id]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const updated = await client.self.updateRealmFields.mutate({ realmId: realm.id, values });
      setFieldState(updated);
      setValues(fieldValues(updated.fields));
      setMessage(t('accountRealmFieldsSaved'));
    } catch {
      setError(t('accountRealmFieldsSaveError'));
    } finally {
      setSaving(false);
    }
  }

  return <Card className="section-card"><div className="section-heading"><div><h2>{t('accountRealmFieldsTitle', { realm: realm.name })}</h2><p>{t('accountRealmFieldsDescription')}</p></div><Badge tone="blue">{realm.slug}</Badge></div>
    {state === 'loading' ? <p className="loading-inline"><span className="spinner"/> {t('accountLoadingFields')}</p> : state === 'error' ? <div className="form-grid"><div className="full"><Notice tone="danger" title={t('accountLoadError')}>{t('accountRealmFieldsLoadError', { realm: realm.name })}</Notice></div></div> : !fieldState || fieldState.fields.length === 0 ? <div className="empty-inline"><p>{t('accountNoRealmFields')}</p></div> : <form className="form-grid" onSubmit={(event) => void save(event)}>{fieldState.fields.map((field) => <RealmFieldInput key={field.key} field={field} value={values[field.key]} disabled={saving} onChange={(value) => setValues((current) => ({ ...current, [field.key]: value }))}/>)}<div className="form-actions full">{message && <span className="saved-message" role="status"><Icon name="check"/> {message}</span>}{error && <span className="field-error" role="alert">{error}</span>}<Button type="submit" disabled={saving}>{saving ? t('accountSaving') : t('accountSaveFields')}</Button></div></form>}
  </Card>;
}

function MutationWarning({ reason }: { reason: string | undefined }) {
  const { t } = useI18n();
  return reason ? <Notice tone="warning" title={t('accountChangesBlocked')}>{reason}</Notice> : null;
}

function MethodsScreen({ principal, client }: { principal: SessionPrincipal; client: IdentityTrpcClient }) {
  const { t } = useI18n();
  const [methods, setMethods] = useState<AuthMethod[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [refresh, setRefresh] = useState(0);
  const [dialog, setDialog] = useState<'password' | 'totp' | 'disable-totp' | 'recovery' | null>(null);
  const [secret, setSecret] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const blocked = mutationBlockReason(principal, 'credential');

  useEffect(() => {
    let active = true;
    setState('loading');
    client.self.authMethods.query().then((rows) => {
      if (!active) return;
      setMethods(rows);
      setState('ready');
    }).catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, [client, refresh]);

  function reload() { setRefresh((current) => current + 1); }
  async function beginTotp() {
    if (blocked) return;
    setMessage('');
    try {
      const result = await client.self.totpSetup.mutate();
      setSecret(result.seed);
      setDialog('totp');
    } catch { setMessage(t('accountTotpSetupError')); }
  }
  async function makeRecoveryCodes() {
    if (blocked) return;
    setMessage('');
    try {
      const result = await client.self.recoveryCodes.mutate();
      setCodes(result.codes);
      setDialog('recovery');
    } catch { setMessage(t('accountRecoveryCodesError')); }
  }

  return <><PageHeader eyebrow={t('accountSecurity')} title={t('accountMethodsTitle')} description={t('accountMethodsDescription')} actions={<Button disabled={Boolean(blocked)} onClick={() => setDialog('password')}>{t('accountChangePassword')}</Button>}/>
    <MutationWarning reason={blocked ? t('accountImpersonationBlocked') : undefined}/>{message && <p className="status-line" role="alert">{message}</p>}
    <Card className="section-card"><div className="section-heading"><div><h2>{t('accountSignIn')}</h2><p>{t('accountSignInDescription')}</p></div></div>
      {state === 'loading' ? <p className="loading-inline"><span className="spinner"/> {t('accountLoadingMethods')}</p> : state === 'error' ? <div className="empty-inline" role="alert"><p>{t('accountMethodsLoadError')}</p></div> : methods.filter((method) => method.kind !== 'totp').length === 0 ? <div className="empty-inline"><p>{t('accountNoMethods')}</p></div> : <div className="settings-list">{methods.filter((method) => method.kind !== 'totp').map((method) => <AuthMethodRow key={method.id} method={method}/>)}</div>}
    </Card>
    <Card className="section-card"><div className="section-heading"><div><h2>{t('accountTwoStepVerification')}</h2><p>{t('accountTwoStepDescription')}</p></div><Button variant="secondary" disabled={Boolean(blocked)} onClick={() => void beginTotp()}>{t('accountAddTotp')}</Button></div>
      {state === 'loading' ? <p className="loading-inline"><span className="spinner"/> {t('accountLoadingTotp')}</p> : state === 'error' ? <div className="empty-inline" role="alert"><p>{t('accountTotpLoadError')}</p></div> : methods.filter((method) => method.kind === 'totp').length === 0 ? <div className="empty-inline"><p>{t('accountNoTotp')}</p></div> : <div className="settings-list">{methods.filter((method) => method.kind === 'totp').map((method) => <AuthMethodRow key={method.id} method={method} action={<Button variant="ghost" size="small" disabled={Boolean(blocked)} onClick={() => setDialog('disable-totp')}>{t('accountRemove')}</Button>}/>)}</div>}
      <div className="recovery-row"><div><strong>{t('accountRecoveryCodes')}</strong><p>{t('accountRecoveryDescription')}</p></div><Button variant="secondary" disabled={Boolean(blocked)} onClick={() => void makeRecoveryCodes()}>{t('accountGenerateNew')}</Button></div>
    </Card>
    <PasswordDialog open={dialog === 'password'} onClose={() => setDialog(null)} client={client} onSuccess={reload}/>
    <TotpDialog open={dialog === 'totp'} onClose={() => setDialog(null)} seed={secret} client={client} onSuccess={reload}/>
    <DisableTotpDialog open={dialog === 'disable-totp'} onClose={() => setDialog(null)} client={client} onSuccess={reload}/>
    <Dialog open={dialog === 'recovery'} title={t('accountSaveCodesTitle')} description={t('accountSaveCodesDescription')} onClose={() => { setDialog(null); setCodes([]); }}><div className="recovery-codes">{codes.map((code) => <code key={code}>{code}</code>)}</div><div className="dialog-actions"><Button onClick={() => { setDialog(null); setCodes([]); }}>{t('accountCodesSaved')}</Button></div></Dialog>
  </>;
}

function AuthMethodRow({ method, action }: { method: AuthMethod; action?: React.ReactNode }) {
  const { t, formatDateTime } = useI18n();
  const date = method.lastUsedAt ?? method.createdAt;
  return <div className="settings-row"><span className="setting-icon"><Icon name={method.kind === 'totp' ? 'shield' : 'key'}/></span><div><strong>{method.label || authMethodName(method.kind, t)}</strong><small>{method.lastUsedAt ? t('accountLastUsed', { date: formatDateTime(date) }) : t('accountCreated', { date: formatDateTime(date) })}</small></div><Badge tone="success">{t('accountActive')}</Badge>{action}</div>;
}

function PasswordDialog({ open, onClose, client, onSuccess }: { open: boolean; onClose: () => void; client: IdentityTrpcClient; onSuccess: () => void }) {
  const { t } = useI18n();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== repeat) { setError(t('accountPasswordMismatch')); return; }
    setSaving(true);
    setError('');
    try {
      await client.self.changePassword.mutate({ password });
      onSuccess();
      onClose();
      setPassword('');
      setRepeat('');
    } catch { setError(t('accountPasswordChangeError')); } finally { setSaving(false); }
  }

  return <Dialog open={open} title={t('accountChangePasswordTitle')} description={t('accountChangePasswordDescription')} onClose={onClose}><form onSubmit={(event) => void submit(event)}><Field label={t('accountNewPassword')} error={error} required><Input type="password" autoComplete="new-password" minLength={12} value={password} disabled={saving} onChange={(event) => setPassword(event.target.value)} required/></Field><Field label={t('accountRepeatPassword')} required><Input type="password" autoComplete="new-password" minLength={12} value={repeat} disabled={saving} onChange={(event) => setRepeat(event.target.value)} required/></Field><div className="dialog-actions"><Button type="button" variant="ghost" disabled={saving} onClick={onClose}>{t('cancel')}</Button><Button type="submit" disabled={saving}>{saving ? t('accountUpdating') : t('accountUpdate')}</Button></div></form></Dialog>;
}

function TotpDialog({ open, onClose, seed, client, onSuccess }: { open: boolean; onClose: () => void; seed: string; client: IdentityTrpcClient; onSuccess: () => void }) {
  const { t } = useI18n();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await client.self.enrollTotp.mutate({ seed, code });
      onSuccess();
      onClose();
      setCode('');
    } catch { setError(t('accountTotpEnrollError')); } finally { setSaving(false); }
  }

  return <Dialog open={open} title={t('accountAddAuthenticatorTitle')} description={t('accountAddAuthenticatorDescription')} onClose={onClose}><div className="secret-box"><code>{secretChunks(seed)}</code><Button variant="ghost" size="small" type="button" aria-label={t('accountCopySecret')} onClick={() => void navigator.clipboard?.writeText(seed)}><Icon name="copy"/> {t('copy')}</Button></div><form onSubmit={(event) => void submit(event)}><Field label={t('accountVerificationCode')} error={error} required><Input inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code} disabled={saving} onChange={(event) => setCode(event.target.value)} required/></Field><div className="dialog-actions"><Button type="button" variant="ghost" disabled={saving} onClick={onClose}>{t('cancel')}</Button><Button type="submit" disabled={saving}>{saving ? t('accountConfirming') : t('accountConfirm')}</Button></div></form></Dialog>;
}

function DisableTotpDialog({ open, onClose, client, onSuccess }: { open: boolean; onClose: () => void; client: IdentityTrpcClient; onSuccess: () => void }) {
  const { t } = useI18n();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await client.self.disableTotp.mutate({ code });
      onSuccess();
      onClose();
      setCode('');
    } catch { setError(t('accountTotpDisableError')); } finally { setSaving(false); }
  }

  return <Dialog open={open} title={t('accountRemoveTotpTitle')} description={t('accountRemoveTotpDescription')} onClose={onClose}><form onSubmit={(event) => void submit(event)}><Field label={t('accountVerificationCode')} error={error} required><Input inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code} disabled={saving} onChange={(event) => setCode(event.target.value)} required/></Field><div className="dialog-actions"><Button type="button" variant="ghost" disabled={saving} onClick={onClose}>{t('cancel')}</Button><Button type="submit" variant="danger" disabled={saving}>{saving ? t('accountRemoving') : t('accountRemoveTotpTitle')}</Button></div></form></Dialog>;
}

function secretChunks(seed: string) { return seed.match(/.{1,4}/g)?.join(' ') ?? seed; }

function SessionsScreen({ principal, client }: { principal: SessionPrincipal; client: IdentityTrpcClient }) {
  const { t, formatDateTime } = useI18n();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [mutationError, setMutationError] = useState('');
  const [pendingId, setPendingId] = useState('');
  const blocked = mutationBlockReason(principal, 'credential');

  useEffect(() => {
    let active = true;
    client.self.sessions.query().then((rows) => {
      if (!active) return;
      setSessions(rows);
      setState('ready');
    }).catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, [client]);

  async function revoke(id: string) {
    if (blocked) return;
    setPendingId(id);
    setMutationError('');
    try {
      await client.self.revokeSession.mutate({ sessionId: id });
      setSessions((current) => current.filter((item) => item.id !== id));
    } catch { setMutationError(t('accountRevokeSessionError')); } finally { setPendingId(''); }
  }

  return <><PageHeader eyebrow={t('accountSecurity')} title={t('accountSessionsTitle')} description={t('accountSessionsDescription')}/><MutationWarning reason={blocked ? t('accountImpersonationBlocked') : undefined}/>{mutationError && <p className="status-line" role="alert">{mutationError}</p>}<Card className="section-card">
    {state === 'loading' ? <p className="loading-inline"><span className="spinner"/> {t('accountLoadingSessions')}</p> : state === 'error' ? <div className="empty-inline" role="alert"><p>{t('accountSessionsLoadError')}</p></div> : sessions.length === 0 ? <div className="empty-inline"><p>{t('accountNoSessions')}</p></div> : <div className="settings-list">{sessions.map((session) => <div className="settings-row session-row" key={session.id}><span className="setting-icon"><Icon name="sessions"/></span><div><strong>{session.current ? t('accountThisDevice') : t('accountWebSession')}</strong><small>{t('accountSessionActivity', { lastSeen: formatDateTime(session.lastSeenAt), expires: formatDateTime(session.absoluteExpiresAt) })}</small></div>{session.current && <Badge tone="success">{t('accountCurrent')}</Badge>}<Button variant="ghost" size="small" disabled={Boolean(blocked) || pendingId === session.id} onClick={() => void revoke(session.id)}>{pendingId === session.id ? t('accountRevoking') : session.current ? t('accountSignOut') : t('accountRevoke')}</Button></div>)}</div>}
  </Card></>;
}

function ConsentsScreen({ principal, client }: { principal: SessionPrincipal; client: IdentityTrpcClient }) {
  const { t, formatDateTime } = useI18n();
  const [consents, setConsents] = useState<Consent[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [mutationError, setMutationError] = useState('');
  const [pendingId, setPendingId] = useState('');
  const blocked = mutationBlockReason(principal, 'credential');

  useEffect(() => {
    let active = true;
    client.self.consents.query().then((rows) => {
      if (!active) return;
      setConsents(rows as unknown as Consent[]);
      setState('ready');
    }).catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, [client]);

  async function revoke(id: string) {
    if (blocked) return;
    setPendingId(id);
    setMutationError('');
    try {
      await client.self.revokeConsent.mutate({ consentId: id });
      setConsents((current) => current.filter((item) => item.id !== id));
    } catch { setMutationError(t('accountRevokeConsentError')); } finally { setPendingId(''); }
  }

  return <><PageHeader eyebrow={t('accountPrivacy')} title={t('accountConsentsTitle')} description={t('accountConsentsDescription')}/><MutationWarning reason={blocked ? t('accountImpersonationBlocked') : undefined}/>{mutationError && <p className="status-line" role="alert">{mutationError}</p>}<Card className="section-card">
    {state === 'loading' ? <p className="loading-inline"><span className="spinner"/> {t('accountLoadingConsents')}</p> : state === 'error' ? <div className="empty-inline" role="alert"><p>{t('accountConsentsLoadError')}</p></div> : consents.length === 0 ? <div className="empty-inline"><p>{t('accountNoConsents')}</p></div> : <DataTable label={t('accountActiveConsents')} headers={[t('accountApplication'), t('accountGrantedAccess'), t('accountDate'), '']}>{consents.map((consent) => <tr key={consent.id}><td><strong>{consent.applicationName ?? consent.clientId ?? t('accountOidcBinding')}</strong><small className="block-code">{[consent.clientId, consent.realmName ?? consent.realmSlug].filter(Boolean).join(' · ')}</small>{consent.bindingId && <small className="block-code">{consent.bindingId}</small>}</td><td><div className="badge-list">{parseList(consent.scopesJson).map((scope) => <Badge key={scope}>{scope}</Badge>)}</div></td><td>{formatDateTime(consent.grantedAt)}</td><td className="cell-actions"><Button variant="ghost" size="small" disabled={Boolean(blocked) || pendingId === consent.id} onClick={() => void revoke(consent.id)}>{pendingId === consent.id ? t('accountRemoving') : t('accountRemove')}</Button></td></tr>)}</DataTable>}
  </Card></>;
}

function WebIdentitiesScreen({ principal, client }: { principal: SessionPrincipal; client: IdentityTrpcClient }) {
  const { t } = useI18n();
  const [identities, setIdentities] = useState<WebIdentity[]>([]); const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading'); const [mutationError, setMutationError] = useState(''); const [pendingId, setPendingId] = useState(''); const [googleDialog, setGoogleDialog] = useState(false); const [googleWithRecovery, setGoogleWithRecovery] = useState(false); const [linking, setLinking] = useState(false);
  const googleOAuthAvailable = useGoogleOAuthAvailable(client); const blocked = mutationBlockReason(principal, 'identity_link');
  useEffect(() => { let active = true; setState('loading'); client.self.webIdentities.query().then((rows) => { if (active) { setIdentities(rows); setState('ready'); } }).catch(() => active && setState('error')); return () => { active = false; }; }, [client]);
  async function unlink(id: string) { if (blocked) return; setPendingId(id); setMutationError(''); try { await client.self.unlinkWebIdentity.mutate({ webIdentityId: id }); setIdentities((current) => current.filter((identity) => identity.id !== id)); } catch { setMutationError(t('accountUnlinkIdentityError')); } finally { setPendingId(''); } }
  async function beginGoogleLink(event: FormEvent) { event.preventDefault(); if (blocked) return; const form = new FormData(event.currentTarget as HTMLFormElement); setLinking(true); setMutationError(''); try { const result = await client.self.beginGoogleLink.mutate({ email: String(form.get('email') ?? ''), password: String(form.get('password') ?? ''), ...(!googleWithRecovery && form.get('totp') ? { totp: String(form.get('totp')) } : {}), ...(googleWithRecovery && form.get('recoveryCode') ? { recoveryCode: String(form.get('recoveryCode')).toUpperCase() } : {}) }); window.location.assign(result.authorizationUrl); } catch { setMutationError(t('accountGoogleLinkError')); } finally { setLinking(false); } }
  return <><PageHeader eyebrow={t('accountMyAccount')} title={t('accountWebIdentitiesTitle')} description={t('accountWebIdentitiesDescription')} actions={googleOAuthAvailable ? <Button disabled={Boolean(blocked)} onClick={() => { setMutationError(''); setGoogleDialog(true); }}>{t('accountLinkGoogle')}</Button> : undefined}/><MutationWarning reason={blocked ? t('accountImpersonationBlocked') : undefined}/>{mutationError && <p className="status-line status-line-error" role="alert">{mutationError}</p>}<Card className="section-card">{state === 'loading' ? <p className="loading-inline"><span className="spinner"/> {t('accountLoadingWebIdentities')}</p> : state === 'error' ? <div className="empty-inline" role="alert"><p>{t('accountWebIdentitiesLoadError')}</p></div> : identities.length === 0 ? <div className="empty-inline"><p>{t('accountNoWebIdentities')}</p></div> : <div className="settings-list">{identities.map((identity) => <div className="settings-row" key={identity.id}><span className="google-mini">{initials(identity.provider)}</span><div><strong>{identity.provider}</strong><small>{identity.email}{identity.emailVerifiedAt ? t('accountEmailVerifiedShort') : ''}</small></div><Badge tone={identity.suspendedAt ? 'warning' : 'blue'}>{identity.suspendedAt ? t('accountSuspended') : t('accountLinked')}</Badge><Button variant="ghost" size="small" disabled={Boolean(blocked) || pendingId === identity.id} onClick={() => void unlink(identity.id)}>{pendingId === identity.id ? t('accountUnlinking') : t('accountUnlink')}</Button></div>)}</div>}</Card><Notice>{t('accountWebIdentityNotice')}</Notice><Dialog open={googleDialog} title={t('accountLinkGoogle')} description={t('accountGoogleReauthDescription')} onClose={() => { if (!linking) setGoogleDialog(false); }}><form onSubmit={(event) => void beginGoogleLink(event)}><Field label={t('email')} required><Input name="email" type="email" autoComplete="username" disabled={linking} required/></Field><Field label={t('password')} required><Input name="password" type="password" autoComplete="current-password" disabled={linking} required/></Field>{googleWithRecovery ? <Field label={t('recoveryCode')}><Input name="recoveryCode" autoComplete="one-time-code" disabled={linking}/></Field> : <Field label={t('verificationCode')} hint={t('optionalTotp')}><Input name="totp" inputMode="numeric" autoComplete="one-time-code" disabled={linking}/></Field>}<button type="button" className="text-button" disabled={linking} onClick={() => setGoogleWithRecovery((current) => !current)}>{googleWithRecovery ? t('useAuthenticator') : t('useRecovery')}</button><div className="dialog-actions"><Button type="button" variant="ghost" disabled={linking} onClick={() => setGoogleDialog(false)}>{t('cancel')}</Button><Button type="submit" disabled={linking}>{linking ? t('accountLinking') : t('googleContinue')}</Button></div></form></Dialog></>;
}
function fieldValues(fields: RealmField[]): Record<string, unknown> { return Object.fromEntries(fields.map((field) => [field.key, field.value ?? null])); }

function authMethodName(kind: string, t: ReturnType<typeof useI18n>['t']): string {
  if (kind === 'password') return t('accountPassword');
  if (kind === 'totp') return t('accountAuthenticatorApp');
  if (kind === 'passkey') return t('accountPasskey');
  return kind;
}

function initials(value: string): string { return value.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || '?'; }

function parseList(value: string): string[] {
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
}
